import { inject, Injectable, Injector } from '@angular/core';
import { IUser } from '../../common/interfaces/user.interface';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, of, take } from 'rxjs';
import { ILoginResponse, IResponse } from '../../common/interfaces/response.interface';
import { Router } from '@angular/router';
import { MediaUploadService } from '../../common/services/media-upload.service';
import { CookieStorageService } from '../../common/helpers/cookie-storage.service';
import { Role } from '../../common/enums/user.enum';
import { NotificationMessageService } from '../../common/services/notification-message.service';
import { UserStatusService } from '../../common/services/user-status.service';

export interface LoginCredentials {
    email: string;
    password: string;
    rememberMe?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class LoginService {
    private baseUrl = environment.baseUrl;
    private http = inject(HttpClient);
    private cookieService = inject(CookieStorageService);
    private router = inject(Router);
    private mediaUploadFileService = inject(MediaUploadService);
    private notificationService = inject(NotificationMessageService);
    private injector = inject(Injector);

    isToasterVisible = new BehaviorSubject<boolean>(true);
    toasterSubjectData = this.isToasterVisible.asObservable();

    login(credentials: LoginCredentials): Observable<IResponse<ILoginResponse>> {
        return this.http.post<IResponse<ILoginResponse>>(`${this.baseUrl}/users/login`, credentials);
    }

    persistAuthSession(data: ILoginResponse, rememberMe: boolean): void {
        this.cookieService.setRememberMePreference(rememberMe);

        const userInfo = {
            firstName: data?.firstName,
            lastName: data?.lastName,
            email: data?.email,
            userType: data?.userType,
            profilePhotoUrl: data?.profilePhotoUrl,
            id: data?.id,
            isNewsletterSubscribed: data?.isNewsletterSubscribed ?? false
        };

        this.notificationService.requestPermission();
        this.cookieService.set('hasUnreadNotifications', data?.hasUnreadNotifications);
        this.mediaUploadFileService.setNotificationCount(data?.hasUnreadNotifications || false);
        this.cookieService.set('accessToken', data?.token);
        this.cookieService.set('userInfo', userInfo);
        this.setSharedLoginCookie();

        if (data?.adminPermissions) {
            this.cookieService.set('adminPermissions', data.adminPermissions);
        }

        if (data?.userType === 'admin') {
            this.cookieService.set('mfaEnabled', data?.mfaEnabled ?? false);
            this.cookieService.set('isMfaGlobalEnabled', data?.isMfaGlobalEnabled ?? false);
        }

        this.mediaUploadFileService.setUserImage(`${environment.s3URL}${data?.profilePhotoUrl}` || '');
        this.userStatusService.startStatusUpdates();
    }

    completeAuthenticatedLogin(returnUrl?: string | null): void {
        this.redirectAuthenticatedUser(returnUrl);
    }

    private get userStatusService(): UserStatusService {
        return this.injector.get(UserStatusService);
    }

    setSharedLoginCookie(): void {
        this.cookieService.setSharedLoginStatus();
    }

    logOut() {
        // Stop user status updates on logout
        // Using dynamic import to avoid circular dependency
        import('../../common/services/user-status.service').then(({ UserStatusService }) => {
            const userStatusService = this.injector.get(UserStatusService);
            userStatusService.stopStatusUpdates();
        });
        
        this.cookieService.remove('accessToken');
        this.cookieService.remove('adminAccessToken');
        this.cookieService.remove('adminUserInfo');
        this.cookieService.remove('userInfo');
        this.cookieService.remove('adminPermissions');
        this.cookieService.remove('mfaEnabled');
        this.cookieService.remove('mfaToken');
        this.cookieService.remove('isMfaGlobalEnabled');
        this.cookieService.remove('subscription');
        this.cookieService.remove('paymentBanner');
        this.cookieService.clearSharedLoginStatus();
        this.mediaUploadFileService.setUserImage('');
        this.router.navigateByUrl('/login');
    }

    isLoggedIn(): boolean {
        return !!this.cookieService.get('accessToken');
    }

    getUserRole(): string {
        const user = this.cookieService.get('userInfo') as IUser;
        return user?.userType ?? '';
    }

    resolveAuthenticatedDestination(returnUrl?: string | null): Observable<string> {
        if (returnUrl) {
            return of(returnUrl);
        }

        const user = this.cookieService.get<IUser>('userInfo');
        if (!user?.userType) {
            return of('/login');
        }

        switch (user.userType) {
            case Role.ADMIN: {
                const mfaEnabled = this.cookieService.get<boolean>('mfaEnabled');
                return of(mfaEnabled ? '/admin-dashboard' : '/mfa-setup');
            }
            case Role.CLIENT:
                return this.resolveClientDestination();
            case Role.CREATOR:
            default:
                return of('/client-dashboard');
        }
    }

    redirectAuthenticatedUser(returnUrl?: string | null): void {
        if (!this.isLoggedIn()) {
            return;
        }

        this.resolveAuthenticatedDestination(returnUrl)
            .pipe(take(1))
            .subscribe((url) => {
                void this.router.navigateByUrl(url);
            });
    }

    private resolveClientDestination(): Observable<string> {
        const subscription = this.cookieService.get<boolean>('subscription');

        if (subscription === true) {
            return of('/client-dashboard');
        }

        if (subscription === false) {
            return of('/plan');
        }

        return this.getSubscriptionStatus().pipe(
            map((response) => {
                const hasSubscription = !!response.data;
                this.cookieService.set('subscription', hasSubscription);
                return hasSubscription ? '/client-dashboard' : '/plan';
            }),
            catchError(() => of('/client-dashboard'))
        );
    }

    getSubscriptionStatus(): Observable<IResponse<null>> {
        return this.http.get<IResponse<null>>(`${this.baseUrl}/client-billing/get-active-subscription`);
    }

    setToasterFlagVisible(flag: boolean) {
        this.isToasterVisible.next(flag);
    }
}
